/**
 * Tree Frog (Devio + UCC) peripheral port budget model — WDT-89 / PIG-310.
 *
 * The UCC has a hard ceiling of 2 Ethernet peripheral ports. Each mic chain and each
 * standalone PoE amplifier consumes one port. This module provides a pure function
 * that takes an area's state and reports current port consumption, so later phases
 * (amplifier quantity calc, overflow messaging, auto-swap) can read from one place.
 *
 * Chain definitions per the Tree Frog Expansion Device Port Accounting spec
 * correction — see Confluence PIG space page 1604223017 §2.2, "Ethernet port rules":
 *   - Ceiling mic chain = 1 head unit (DCM-X/DCM-XA) + up to 1 optional expander
 *     (DCM-XEX). Max 2 mic elements.
 *   - Table mic chain   = 1 head unit (DTM-X) + up to 1 optional expander (DTM-XEX).
 *     Max 2 mic elements — the SAME cap as ceiling, not 3. (The original WDT-89 model
 *     assumed a table chain could hold 2 expanders; the spec says every Tree Frog
 *     chain, regardless of mic family, is capped at one head + one expander.)
 *   - DCM-XA = ceiling chain whose base unit has an integrated PoE amp; consumes
 *     1 port but provides amp channels (no separate standalone amp port required
 *     unless speaker count exceeds its capacity).
 *   - Standalone DPA-450BP amp = 1 port, supports up to 8 speakers.
 *
 * Bar mics (USB to UCC) do NOT consume a peripheral port.
 *
 * Non-Devio designs (Tesira / Parlé) are not subject to this budget — the helper
 * returns `applicable: false` and reports `used: 0` so callers can early-out
 * without branching themselves.
 *
 * Phase 1 status (this file): chain counting is complete; standalone amp port
 * accounting and DCM-XA detection are stubbed (return 0) until Phase 3 wires the
 * amplifier quantity calc and Phase 5 wires the auto-swap. Both stubs are
 * documented in-line so the wiring can be done without re-deriving the model.
 */
import { MIC_CATEGORIES, getPrimaryMicType } from "types/models/microphone";

/** UCC has exactly 2 Ethernet peripheral ports. Hard ceiling. */
export const UCC_PERIPHERAL_PORT_BUDGET = 2;

/** Ceiling mic chain holds 1 base + 1 expansion head = 2 mic elements max. */
export const CEILING_CHAIN_MAX_MICS = 2;

/** Table mic chain holds 1 base + up to 1 expansion head = 2 mic elements max (same cap as ceiling). */
export const TABLE_CHAIN_MAX_MICS = 2;

/**
 * Catalog modelName prefix used to tag Devio Tree Frog ceiling mics with integrated
 * amplifiers. Used by `countDcmxa` to detect amp-bearing chains in the layout.
 */
const DCMXA_MODEL_PREFIX = 'Parlé DCM-XA';

/**
 * Count the number of Ethernet mic chains the primary microphone configuration
 * occupies. Bar mics use USB to the UCC and are exempt from the port budget.
 *
 * @param {Object} area
 * @returns {number}
 */
const countPrimaryMicChains = (area) => {
  const model = area?.microphones?.model;
  if (!model || MIC_CATEGORIES.BAR.has(model)) return 0;
  const qty = area.microphones?.qty ?? area.microphones?.layout?.length ?? 0;
  if (qty <= 0) return 0;
  const chainMax = MIC_CATEGORIES.TABLETOP.has(model) ? TABLE_CHAIN_MAX_MICS : CEILING_CHAIN_MAX_MICS;
  return Math.ceil(qty / chainMax);
};

/**
 * Count chains for the expansion microphone set, when one is configured. Ceiling /
 * pendant expansion uses the ceiling chain size; table expansion uses the table
 * chain size. Returns 0 when no expansion type is active.
 *
 * @param {Object} area
 * @returns {number}
 */
const countExpansionMicChains = (area) => {
  const type = area?.expansionMicType;
  if (type !== 'ceiling' && type !== 'table') return 0;
  const qty = area.expansionMicrophones?.qty ?? area.expansionMicrophones?.layout?.length ?? 0;
  if (qty <= 0) return 0;
  const chainMax = type === 'table' ? TABLE_CHAIN_MAX_MICS : CEILING_CHAIN_MAX_MICS;
  return Math.ceil(qty / chainMax);
};

/**
 * Count DCM-XA mic elements in the area's layouts. Each DCM-XA is a chain base unit
 * with an integrated amplifier — Phase 5's auto-swap inspects this number to decide
 * whether a standalone amp can be retired.
 *
 * Returns 0 if no layouts are populated with DCM-XA entries. Phase 1 callers (amp
 * quantity calc) should treat this as authoritative once it's non-zero.
 *
 * @param {Object} area
 * @returns {number}
 */
const countDcmxa = (area) => {
  const isDcmxa = (mic) => typeof mic?.modelName === 'string' && mic.modelName.startsWith(DCMXA_MODEL_PREFIX);
  const primary = (area?.microphones?.layout ?? []).filter(isDcmxa).length;
  const expansion = (area?.expansionMicrophones?.layout ?? []).filter(isDcmxa).length;
  return primary + expansion;
};

/**
 * The only primary speaker model sold in the Devio UCX room bundles
 * (`MRB-UCX3000T-VIDI280-*`, `includes.spkrModel: 'C-IC6'`): the standard
 * Desono C-IC6 (`constants.equipment.speakers.ceilingspkr`). The C-IC6LP and
 * C-IC6LP-TAA variants are NOT offered in any UCX bundle.
 */
export const UCX_BUNDLE_SPEAKER_MODEL = 'ceilingspkr';

/**
 * Speaker model keys that never disqualify a Devio UCX design: the bar's own
 * integrated speaker (`barspkr`) rides the bar over USB, not a UCX peripheral.
 */
const UCX_NEUTRAL_SPEAKER_MODELS = new Set(['barspkr']);

/**
 * Per-amplifier speaker capacity by speaker group — Tree Frog Expansion Device
 * Port Accounting spec §2.3 / §5.2 `SPEAKER_CAPS`:
 *   - ceiling (C-IC6, C-IC6LP, C-IC6LP-TAA, 8Ω): 2 per channel → DCM-XA 4, DPA-450BP 8
 *   - pendant (P6-SM, 6Ω): 1 per channel → DCM-XA 2, DPA-450BP 4
 *   - wall (any model): the DCM-XA never powers wall speakers; DPA-450BP only,
 *     held to 2 per design by policy (wall placement math doesn't support more yet).
 */
export const UCX_SPEAKER_CAPS = {
  ceiling: { headAmp: 4, dpa: 8 },
  pendant: { headAmp: 2, dpa: 4 },
  wall: { headAmp: 0, dpa: 2 },
};

/**
 * Speaker group ('ceiling' | 'pendant' | 'wall') of a speaker config, keyed on
 * its mounting type with a model-key fallback for legacy areas that don't carry
 * one. Returns null for no / unknown speakers (including the bar's own speaker).
 *
 * @param {Object|null|undefined} speakers - `area.speakers`
 * @returns {'ceiling'|'pendant'|'wall'|null}
 */
export const getUcxSpeakerGroup = (speakers) => {
  const model = speakers?.model;
  if (!model || UCX_NEUTRAL_SPEAKER_MODELS.has(model)) return null;
  const mounting = speakers.mountingType;
  if (mounting === 'wall' || mounting === 'pendant' || mounting === 'ceiling') return mounting;
  if (model.startsWith('wallspkr')) return 'wall';
  if (model.startsWith('pendantspkr') || (speakers.modelName || '').includes('P6-SM')) return 'pendant';
  if (model.startsWith('ceilingspkr')) return 'ceiling';
  return null;
};

/**
 * Speaker capacities for a NON-BAR (ceiling / table primary mic) design's own
 * speakers. Unknown / missing speaker data falls back to the ceiling group so
 * legacy areas keep their previous behaviour.
 */
const primarySpeakerCaps = (area) => UCX_SPEAKER_CAPS[getUcxSpeakerGroup(area?.speakers) ?? 'ceiling'];

/**
 * True when a ceiling / table PRIMARY mic design (non-bar) uses a primary
 * speaker Devio UCX can't drive at all. Per the Tree Frog UC Platform &
 * Equipment Selection Logic spec §5.3, every speaker model in the catalog is
 * allowed on the non-bar Devio UCX path — ceiling (C-IC6 family), pendant
 * (P6-SM) and wall — each with its own capacity (`UCX_SPEAKER_CAPS`), enforced
 * by the port budget (`checkPortOverflow`). So this only fires for a speaker
 * that belongs to none of those groups; such a design is solved like a
 * port-budget overflow: Tesira à la carte + UCC-UCX1000-T (USB).
 *
 * The standard-C-IC6-only restriction applies to ROOM BUNDLE matching alone
 * (see `UCX_BUNDLE_SPEAKER_MODEL`), not to whether a design can use Devio UCX.
 *
 * Scoped to non-bar designs only. A missing `speakers.model` is treated as
 * compatible so this never disqualifies a design on absent data.
 *
 * @param {Object|null|undefined} area
 * @returns {boolean}
 */
export const isUcxSpeakerIncompatible = (area) => {
  if (!area) return false;
  const micModel = area.microphones?.model;
  if (!micModel || MIC_CATEGORIES.BAR.has(micModel)) return false;
  const spkrModel = area.speakers?.model;
  if (!spkrModel || UCX_NEUTRAL_SPEAKER_MODELS.has(spkrModel)) return false;
  return getUcxSpeakerGroup(area.speakers) === null;
};

/**
 * True when an Android design must route its peripherals through a Tesira DSP
 * + UCC-UCX1000-T (USB) instead of the Devio UCX hub — either the UCC port
 * budget is exceeded (`isPortOverflow`) or the primary speaker isn't one the
 * Devio UCX bundles support (`isUcxSpeakerIncompatible`).
 *
 * @param {Object|null|undefined} area
 * @returns {boolean}
 */
export const requiresTesiraUsbFallback = (area) =>
  isUcxSpeakerIncompatible(area) || isPortOverflow(area);

/**
 * Determines whether Devio UCX (Tree Frog) is a valid platform option for the
 * area's current configuration. Powers the WDT-90 platform-eligibility flow —
 * shape lets the UI decide:
 *   - eligible === false   → don't present UCX as an option (and optionally toast
 *                            with `reason` + `suggestion`)
 *   - eligible === true    → UCX is one of the valid platforms
 *   - exclusive === true   → UCX is the ONLY valid platform; the platform
 *                            selector should be hidden and UCX auto-selected
 *                            (bar + expansion mics confirmed via recommendation flow)
 *
 * The helper is pure — it does NOT consult the user's current Platform / Product
 * Family selection. Callers decide what to do with the eligibility result based
 * on that.
 *
 * Compatibility filters (always disqualify, regardless of port budget):
 *   - Ceiling-tile mics (BMA360D) — Dante-based, not Ethernet peripheral
 *   - Pendant mics (TCM-1) — no Tree Frog pendant SKU exists yet
 *
 * Bar designs split by expansion state:
 *   - Bar + expansion mics added (ceiling or table, any opt-in path) → exclusive
 *   - Bar + expansion speakers added (ceiling or pendant) → exclusive
 *   - Bar-only / bar + camera (no expansion) → eligible (one of N platforms)
 *
 * Exclusivity no longer distinguishes recommendation-flow acceptance from manual
 * opt-in — either path leaves the design equally dependent on Devio UCX, so both
 * block a later switch back to Windows (see `setPlatform` in reduxModules/ducks/room.ts).
 *
 * Exclusivity does NOT hold once the design overflows the port budget — this
 * is a defensive backstop, not the primary mechanism, though. Per the Tree
 * Frog UC Platform & Equipment Selection Logic spec (PIG space page
 * 1605271615), TC-05 (bar + expansion mics/speakers) has NO Tesira equivalent
 * to fall back to at all — unlike TC-08 (plain ceiling/table primary
 * mic-count overflow, non-bar), which does. Rather than let a bar +
 * expansion design overflow and then silently swap Platform/Family/mic
 * hardware out from under the user, `addExpansionMic` / `addExpansionSpeaker`
 * / `setExpansionMicLayout` / `setExpansionSpeakerLayout` (reduxModules/ducks/room.ts)
 * cap counts via `maxExpansionMicCountFor` / `maxExpansionSpeakerCountFor`
 * below so a bar + expansion design can never reach `isPortOverflow` through
 * normal use in the first place. This function's overflow branch (uniform
 * `fallback: 'port-overflow'` regardless of shape) only matters for the
 * non-bar TC-08 case in practice — it stays uniform (not split by shape) as a
 * simple, already-safe fallback for any bar + expansion design that reaches
 * this state anyway (e.g. a legacy saved design from before the caps existed).
 *
 * Non-bar designs (ceiling / table primary) are eligible iff the port budget
 * fits for the selected speaker group (any catalog speaker — spec §5.3);
 * otherwise they get `fallback: 'port-overflow'` (Tesira + UCC-UCX1000-T).
 *
 * @param {Object|null|undefined} area
 * @returns {{
 *   eligible: boolean,
 *   exclusive: boolean,
 *   fallback?: 'port-overflow' | 'speaker-incompatible',
 *   reason?: string,
 *   suggestion?: string,
 * }}
 */
export const isDevioUcxEligible = (area) => {
  if (!area) return { eligible: false, exclusive: false };

  const micModel = area.microphones?.model;
  const isBar = Boolean(micModel) && MIC_CATEGORIES.BAR.has(micModel);
  const isCeilingTile = Boolean(micModel) && MIC_CATEGORIES.CEILING_TILE.has(micModel);
  const isPendant = Boolean(micModel) && MIC_CATEGORIES.PENDANT.has(micModel);

  // Hard compatibility filters — Tree Frog isn't a valid platform for these
  // mic families regardless of port budget. BMA360D is Dante (different bus);
  // there's no Tree Frog pendant SKU defined today.
  if (isCeilingTile) {
    return {
      eligible: false,
      exclusive: false,
      reason: 'Ceiling-tile microphones (BMA360D) are not compatible with Devio UCX.',
    };
  }
  if (isPendant) {
    return {
      eligible: false,
      exclusive: false,
      reason: 'Pendant microphones are not currently supported on Devio UCX.',
    };
  }

  // Bar + expansion mics and/or expansion speakers make Tree Frog (Devio UCX /
  // Android) the only platform that can carry the design, regardless of how the
  // expansion was added (auto-recommendation acceptance or manual opt-in) — and
  // regardless of the port-budget check below. Computed up front so the
  // port-overflow branch can carry the same exclusivity instead of unwinding it.
  const hasExpansionMics = area.expansionMicType === 'ceiling' || area.expansionMicType === 'table';
  const hasExpansionSpeakers = area.expansionSpeakerType === 'ceiling' || area.expansionSpeakerType === 'pendant';
  const isExclusiveBarShape = isBar && (hasExpansionMics || hasExpansionSpeakers);

  // Unsupported primary speaker (non-bar only — see `isUcxSpeakerIncompatible`).
  // Same soft-failure shape as port overflow: Android stays valid, but the
  // design is solved with Tesira à la carte + UCC-UCX1000-T (USB).
  if (isUcxSpeakerIncompatible(area)) {
    return {
      eligible: false,
      exclusive: false,
      fallback: 'speaker-incompatible',
      reason: 'The selected speakers are not supported by Devio UCX.',
      suggestion: 'Select a ceiling, pendant or wall speaker supported by Devio UCX.',
    };
  }

  // Port budget check (AC 3 & 5) — overflow disqualifies the clean Devio UCX
  // bundle from the platform radio. `isPortOverflow` nets in the speaker amp
  // ports too, not just the mic-chain count, so a full-mic-port
  // design that still needs a speaker amp is correctly disqualified.
  //
  // Uniform regardless of bar shape — see the function doc above. A bar +
  // expansion design should never actually reach this branch (the mic/speaker
  // count reducers cap counts before they can overflow), so this is a
  // defensive fallback, not the primary mechanism for that shape.
  //
  // `exclusive: false` here even for a bar+expansion shape (`isExclusiveBarShape`
  // is intentionally NOT read on this branch) — overflow always unlocks
  // Windows/Family regardless of how exclusivity would otherwise apply.
  if (isPortOverflow(area)) {
    return {
      eligible: false,
      exclusive: false,
      fallback: 'port-overflow',
      reason: 'Configuration exceeds UCC peripheral port limits.',
      suggestion: 'Reduce expansion microphones or speakers to fit within UCC port limits.',
    };
  }

  if (isBar) {
    // Surfaced as exclusive so the Platform selector hides and `setPlatform`
    // blocks a manual switch back to Windows.
    if (isExclusiveBarShape) {
      return { eligible: true, exclusive: true };
    }

    // Bar-only / bar + camera with no expansion — eligible by default (user
    // chooses Platform = Windows vs Android via the cascade in Phase B).
    return { eligible: true, exclusive: false };
  }

  // Non-bar primary mic (ceiling / table). Already passed the compatibility
  // filters above and the port budget check — eligible alongside Tesira / SCX.
  return { eligible: true, exclusive: false };
};

/**
 * true when the design cannot fit within the UCC port budget — either the mic
 * chains alone spill past both ports, OR the speakers demand more standalone
 * DPA-450BP amp ports than remain after the mic chains claim theirs. Drives the
 * Tesira + UCC-UCX1000-T (USB) fallback path.
 *
 * PIG-594: previously this only checked mic-chain overflow (`exceeds`), so a
 * design that filled both ports with mic chains AND needed a speaker amp (e.g.
 * a table-mic room with C-IC6 speakers) reported "fits", the room bundle was
 * selected, and the amp had nowhere to connect. Netting the amp ports in — the
 * same accounting `checkPortOverflow` already performs (including the DCM-XA amp
 * swap that folds the amp into a ceiling chain) — makes the fallback fire so the
 * design goes à la carte via Tesira instead of committing to an unbuildable bundle.
 */
export const isPortOverflow = (area) => {
  if (!area) return false;
  // This is a hypothetical check ("if this design were on Devio UCX/Tree Frog,
  // would it overflow the UCC's port budget?") used by `isDevioUcxEligible` to
  // decide whether UCX is offerable — so it must normalize BOTH fields that
  // `computePortUsage` now gates on, not just `productFamily`, or the budget
  // model would report `applicable: false` and never actually check anything.
  const normalized = { ...area, productFamily: 'Devio', platform: 'Android' };
  // Mic chains alone overrun both ports.
  if (computePortUsage(normalized).exceeds) return true;
  // Mic chains fit, but the speaker amp(s) have no port left.
  return checkPortOverflow(normalized) !== null;
};

/**
 * Fallback search ceiling for `maxExpansionMicCountFor` / `maxExpansionSpeakerCountFor`
 * when a caller passes a non-finite `maxCap` (e.g. `constants.maxExpansionMics ?? Infinity`
 * with `constants` unavailable). The UCC's 2-port budget saturates chain/amp capacity
 * far below this — it exists only to keep `Array(n).fill(...)` from throwing
 * `RangeError: Invalid array length` on `Array(Infinity)`.
 */
const PRACTICAL_SEARCH_CEILING = 20;

/** Clamp an untrusted maxCap to a safe, finite, non-negative integer for the search loop below. */
const clampSearchCeiling = (maxCap) =>
  Number.isFinite(maxCap) ? Math.max(0, Math.floor(maxCap)) : PRACTICAL_SEARCH_CEILING;

/**
 * Largest expansion MIC count (0..maxCap) that does NOT push a bar +
 * expansion design into port-overflow, given the area's CURRENT expansion
 * speaker count. Tesira has no fallback for a bar + expansion overflow
 * (TC-05, see `isDevioUcxEligible`'s doc comment) — rather than let the
 * design reach that state and then swap Platform/Family/mic hardware out
 * from under the user, `addExpansionMic` / `setExpansionMicLayout`
 * (reduxModules/ducks/room.ts) cap counts at this value instead.
 *
 * Brute-forces candidate counts against `isPortOverflow` (small range, 0 to
 * `maxCap`, today 4) rather than re-deriving the port/amp-swap math — reuses
 * the exact same accounting `isPortOverflow` already applies elsewhere, so
 * this can never drift out of sync with what actually overflows.
 *
 * @param {Object} area - area BEFORE the mic count changes (current speaker count read from here)
 * @param {number} maxCap - static UI cap (constants.maxExpansionMics)
 * @returns {number}
 */
export const maxExpansionMicCountFor = (area, maxCap) => {
  const cap = clampSearchCeiling(maxCap);
  for (let n = cap; n >= 0; n--) {
    const candidate = {
      ...area,
      expansionMicrophones: { ...area?.expansionMicrophones, qty: n, layout: Array(n).fill({}) },
    };
    if (!isPortOverflow(candidate)) return n;
  }
  return 0;
};

/**
 * Largest expansion SPEAKER count (0..maxCap) that does NOT push a bar +
 * expansion design into port-overflow, given the area's CURRENT expansion
 * mic count. Mirrors `maxExpansionMicCountFor` — see its doc comment for why
 * this brute-forces against `isPortOverflow` instead of re-deriving the math.
 *
 * @param {Object} area - area BEFORE the speaker count changes (current mic count read from here)
 * @param {number} maxCap - static UI cap (constants.maxExpansionSpeakers)
 * @returns {number}
 */
export const maxExpansionSpeakerCountFor = (area, maxCap) => {
  const cap = clampSearchCeiling(maxCap);
  for (let n = cap; n >= 0; n--) {
    const candidate = {
      ...area,
      expansionSpeakers: { ...area?.expansionSpeakers, qty: n, layout: Array(n).fill({}) },
    };
    if (!isPortOverflow(candidate)) return n;
  }
  return 0;
};

/**
 * Maximum number of standalone PoE amplifier (DPA-450BP) ports available for this
 * area. Each amp consumes 1 UCC port; mic chains consume the rest, so for Tree
 * Frog this returns `2 - (primaryMicChains + expansionMicChains)`, clamped at 0.
 * Non-Devio designs return `Infinity` so callers wrapping their amp count with
 * `Math.min(desired, max)` are no-ops on Tesira / Parlé designs (no port budget
 * applies).
 *
 * Bar-only design (0 mic chains) leaves both ports for amps; one mic chain
 * leaves one port; two mic chains leave zero — when an amp is still required
 * after exhausting the budget, the overflow toast surfaces that to the user.
 *
 * @param {Object|null|undefined} area
 * @returns {number}
 */
export const maxStandaloneAmpPortsFor = (area) => {
  const usage = computePortUsage(area);
  if (!usage.applicable) return Infinity;
  return Math.max(0, 2 - (usage.breakdown.primaryMicChains + usage.breakdown.expansionMicChains));
};

/**
 * Per-DCM-XA speaker capacity, derived from `UCX_SPEAKER_CAPS` (the single
 * source of truth): 2 amp channels — C-IC6 family 2 per channel = 4, pendant
 * P6-SM 1 per channel = 2 (spec §4.3 Case C).
 */
export const DCMXA_SPEAKER_CAPACITY = UCX_SPEAKER_CAPS.ceiling.headAmp;
const DCMXA_SPEAKER_CAPACITY_PENDANT = UCX_SPEAKER_CAPS.pendant.headAmp;

/**
 * Standalone DPA-450BP speaker capacity for the C-IC6 family (8), derived from
 * `UCX_SPEAKER_CAPS` — pendant uses `UCX_SPEAKER_CAPS.pendant.dpa` (4).
 */
export const DPA_SPEAKER_CAPACITY = UCX_SPEAKER_CAPS.ceiling.dpa;

/**
 * PIG-651 follow-up: expansion mics/speakers only exist for bar-primary
 * designs (see room.ts's setExpansionMicType guards), so `expansionSpeakerType`
 * is the single source of truth for "are the speakers being amplified here
 * pendant P6-SM or ceiling C-IC6" in this module — there is no bar-primary
 * pendant speaker field to also check.
 */
export const isPendantExpansionSpeakers = (area) => area?.expansionSpeakerType === 'pendant';

/**
 * Per-amp capacities (`UCX_SPEAKER_CAPS`) for a bar design's expansion speakers:
 * the C-IC6 family, or pendant P6-SM on a legacy design that still has them.
 */
export const expansionSpeakerCaps = (area) =>
  UCX_SPEAKER_CAPS[isPendantExpansionSpeakers(area) ? 'pendant' : 'ceiling'];

/**
 * PIG-595 — spec §4.1 "Ceiling mic + speaker combinations". Decide how to amplify
 * the speakers of a Tree Frog (Devio) ceiling-mic design given the ceiling mic
 * base units available and the free standalone-amp ports left after mic chains.
 *
 * The principle (per the spec) is: use plain DCM-X unless speaker demand requires
 * amplification, then reach for the cheapest cover:
 *   - A single DCM-XA head (integrated amp, 4 speakers) is cheaper than a
 *     standalone DPA-450BP box, so small demand promotes a base unit to DCM-XA.
 *   - Once demand exceeds one head's 4 channels AND a peripheral port is free, a
 *     standalone DPA-450BP (8 speakers) is the efficient bulk cover
 *     (row "1–2 mics, 5–8 speakers → DCM-X + DPA-450BP").
 *   - When both ports are consumed by mic chains (3–4 mics → 2 chains), no port is
 *     left for a DPA, so base units MUST be promoted to DCM-XA
 *     (row "3–4 mics, 1–8 speakers → DCM-XA + DCM-XA"). This is the PIG-595 bug:
 *     the calc previously dropped the standalone amp at the port cap and left the
 *     speakers unamplified instead of promoting the heads.
 *
 * Pure function — takes counts, returns a plan. `uncovered > 0` means the design
 * can't be amplified within the port budget (row "3–4 mics, 9–12 → not possible");
 * the caller leaves it to the existing overflow reporting.
 *
 * @param {{ baseUnits: number, speakers: number, freePorts: number }} input
 *   - baseUnits: ceiling mic base units that can be promoted to DCM-XA
 *   - speakers:  speakers still needing external amplification
 *   - freePorts: UCC peripheral ports free for standalone DPA-450BP amps
 * @returns {{ xaBaseUnits: number, dpaQty: number, uncovered: number }}
 */
export const planCeilingAmplification = ({
  baseUnits = 0,
  speakers = 0,
  freePorts = 0,
  // Per-unit speaker capacity for the design's speaker group (`UCX_SPEAKER_CAPS`).
  // Defaults are the ceiling C-IC6 values; pendant P6-SM is 2 / 4, and wall
  // speakers are 0 / 2 (a DCM-XA never powers wall speakers).
  headAmpCapacity = DCMXA_SPEAKER_CAPACITY,
  dpaCapacity = DPA_SPEAKER_CAPACITY,
}) => {
  if (speakers <= 0) return { xaBaseUnits: 0, dpaQty: 0, uncovered: 0 };

  let xaBaseUnits = 0;
  let dpaQty = 0;

  const dpaNeeded = Math.ceil(speakers / dpaCapacity);
  if (speakers > headAmpCapacity && freePorts >= dpaNeeded) {
    // Demand exceeds one head's capacity and there are enough free ports —
    // standalone DPA-450BP is the cheaper bulk cover.
    dpaQty = dpaNeeded;
  } else if (headAmpCapacity > 0) {
    // Promote base units to DCM-XA first (built-in amp, no extra box / port), then
    // cover any remainder with a DPA on whatever ports remain.
    xaBaseUnits = Math.min(baseUnits, Math.ceil(speakers / headAmpCapacity));
    const remaining = speakers - xaBaseUnits * headAmpCapacity;
    if (remaining > 0) dpaQty = Math.min(freePorts, Math.ceil(remaining / dpaCapacity));
  } else {
    // No head-amp option (wall speakers): DPA-450BP only, on whatever ports remain.
    dpaQty = Math.min(freePorts, dpaNeeded);
  }

  const covered = xaBaseUnits * headAmpCapacity + dpaQty * dpaCapacity;
  return { xaBaseUnits, dpaQty, uncovered: Math.max(0, speakers - covered) };
};

/**
 * Evaluate whether the standalone PoE amplifier can be replaced with the
 * amplified DCM-XA ceiling head unit (PIG-595). When a Tree Frog design has
 * ceiling expansion mics AND a standalone amp would otherwise be added, the
 * DCM-XA option folds the amp into the mic chain — saving a peripheral port.
 * The swap only applies when the DCM-XA channels can cover all speakers
 * needing amplification.
 *
 * Table expansion mics (DTM-X) have no amplified equivalent — there is no
 * DTM-XA hardware — so this always reports `not-applicable` for
 * `expansionMicType === 'table'`; table-mic designs route speakers through
 * the standalone DPA-450BP exclusively.
 *
 * Returns `{ swapValid: true, ... }` when the swap is safe to apply silently;
 * otherwise returns `{ swapValid: false, reason, ... }` so the caller can leave
 * the standalone amp in place and let the existing overflow toast notify the
 * user when the port budget is still violated.
 *
 * @param {Object|null|undefined} area
 * @returns {{
 *   swapValid: boolean,
 *   reason: string,
 *   dcmxaChainsNeeded?: number,
 *   speakerChannelsFromSwap?: number,
 *   totalSpeakersNeedingAmps?: number,
 * }}
 */
export const evaluateAmpSwap = (area) => {
  // DCM-XA (the integrated-amp head this swap promotes to) is Tree Frog /
  // UCX-specific hardware — Devio SCX has no DCM-X family at all, so gate on
  // the same Devio-AND-Android condition `computePortUsage` uses, not
  // `productFamily === 'Devio'` alone.
  if (!area || area.productFamily !== 'Devio' || area.platform !== 'Android') {
    return { swapValid: false, reason: 'not-tree-frog' };
  }
  if (area.expansionMicType !== 'ceiling') {
    return { swapValid: false, reason: 'not-applicable' };
  }

  const expansionMicCount = area.expansionMicrophones?.layout?.length ?? area.expansionMicrophones?.qty ?? 0;
  if (expansionMicCount === 0) {
    return { swapValid: false, reason: 'no-expansion-mics' };
  }

  // Speakers needing external amplification. Bar primary excludes its internal
  // speakers (they're driven by the bar itself); non-bar primary includes both
  // primary and expansion speakers.
  const isBarPrimary = Boolean(area.microphones?.model) && MIC_CATEGORIES.BAR.has(area.microphones.model);
  const primarySpkrs = isBarPrimary ? 0 : (area.speakers?.layout?.length ?? area.speakers?.qty ?? 0);
  const expansionSpkrs = area.expansionSpeakers?.layout?.length ?? area.expansionSpeakers?.qty ?? 0;
  const totalSpeakersNeedingAmps = primarySpkrs + expansionSpkrs;

  // Cost optimization (spec §4.1 "the tool selects DCM-X or DCM-XA based on how
  // many speaker amp channels that port must provide"): promote only as many
  // chains to the amplified head (DCM-XA) as the actual speaker demand
  // requires, capped at the chains actually available — NOT every chain. A
  // chain with no amp demand left over stays plain (cheaper, no wasted amp
  // capacity). Previously this always promoted every chain once the swap fit at
  // all (e.g. 2 chains + 4 speakers promoted both, when 1 chain's capacity
  // already covers all 4 speakers), producing an equipment list with more XA
  // units than the design actually needs.
  //
  // Pendant P6-SM expansion speakers halve the per-chain capacity (1/channel
  // instead of 2/channel) — a chain that fully covers a ceiling C-IC6 quota only
  // covers half as many pendant speakers. Without this, e.g. 2 ceiling chains +
  // 6 pendant speakers wrongly reported "swap-fits" (2 chains × 4 = 8 ≥ 6) when
  // the real capacity is 2 chains × 2 = 4 — 2 speakers short with no port left
  // for a standalone DPA-450BP to cover the gap.
  const pendant = isPendantExpansionSpeakers(area);
  const dcmxaCapacity = pendant ? DCMXA_SPEAKER_CAPACITY_PENDANT : DCMXA_SPEAKER_CAPACITY;
  const totalChains = Math.ceil(expansionMicCount / CEILING_CHAIN_MAX_MICS);
  const dcmxaChainsNeeded = Math.min(totalChains, Math.ceil(totalSpeakersNeedingAmps / dcmxaCapacity));
  const speakerChannelsFromSwap = dcmxaChainsNeeded * dcmxaCapacity;

  if (totalSpeakersNeedingAmps === 0) {
    // No speakers to amplify means no standalone amp would have been added;
    // there's nothing to swap.
    return {
      swapValid: false,
      reason: 'no-speakers-to-amplify',
      dcmxaChainsNeeded,
      speakerChannelsFromSwap,
      totalSpeakersNeedingAmps: 0,
    };
  }

  if (speakerChannelsFromSwap >= totalSpeakersNeedingAmps) {
    return {
      swapValid: true,
      reason: 'swap-fits',
      dcmxaChainsNeeded,
      speakerChannelsFromSwap,
      totalSpeakersNeedingAmps,
    };
  }

  return {
    swapValid: false,
    reason: 'insufficient-channels',
    dcmxaChainsNeeded,
    speakerChannelsFromSwap,
    totalSpeakersNeedingAmps,
  };
};

/**
 * Device count the way `calculateEquipmentTable` reads it: the placed layout when
 * it has entries, otherwise `qty` (e.g. the estimated qty a capacity-fallback
 * promotion commits with an intentionally empty layout). Used by
 * `checkPortOverflow` so the port check and the BOM always size from the same count.
 */
const placedCount = (device) => {
  const placed = device?.layout?.length ?? 0;
  return placed > 0 ? placed : (device?.qty ?? 0);
};

/**
 * Detect whether the area's speaker + amplifier configuration would exceed the
 * UCC peripheral port budget, and if so, return a structured report with a
 * suggested alternative the user can act on. Returns `null` when the design
 * fits (or when the budget doesn't apply, i.e. non-Devio).
 *
 * Speaker counting differs by primary mic type:
 *   - Bar primary: only expansion speakers consume external amp ports. The bar
 *     has its own internal amplification for the speakers it ships with.
 *   - Non-bar primary: primary + expansion speakers both need external amps.
 *
 * The suggestion text is shaped by which kind of consumer is occupying the
 * port — expansion mic chains can sometimes be replaced with amplified heads
 * (DCM-XA) or removed; primary mic chains generally can't be reduced without
 * changing the design's microphone configuration.
 *
 * @param {Object|null|undefined} area
 * @returns {{
 *   desiredAmps: number,
 *   availableAmpPorts: number,
 *   deficit: number,
 *   speakersNeedingAmps: number,
 *   suggestion: string,
 *   message: string,
 * } | null}
 */
export const checkPortOverflow = (area) => {
  const usage = computePortUsage(area);
  if (!usage.applicable) return null;

  // If the DCM-XA swap is valid the equipment calc drops the standalone amp
  // entirely — the chains' integrated amps cover the speakers and no extra port
  // is needed. Without this short-circuit, `desiredAmps` would be computed
  // against a usage breakdown that doesn't know about the swap, and the toast
  // would fire about a port conflict the calc already resolved silently.
  if (evaluateAmpSwap(area).swapValid) return null;

  const isBarPrimary = Boolean(area?.microphones?.model) && MIC_CATEGORIES.BAR.has(area.microphones.model);
  const primarySpkrs = isBarPrimary ? 0 : placedCount(area?.speakers);
  const expansionSpkrs = placedCount(area?.expansionSpeakers);
  const speakersNeedingAmps = primarySpkrs + expansionSpkrs;

  const availableAmpPorts = usage.remaining;

  // Standalone DPA-450BP amp ports the design actually demands — port accounting
  // spec §5.2 step 1, applied to whichever Ethernet mic chains the design has:
  // the primary ceiling / table mics of a non-bar design, or a bar's expansion
  // ceiling / table mics. First option that fits wins:
  //   1. head amps only — ceiling mic heads promoted to DCM-XA (0 DPA)
  //   2. one DPA-450BP alone
  //   3. ceiling mics: one DPA-450BP + overflow on a port-1 DCM-XA
  // Anything beyond that can't be built on two ports, so the demand is reported
  // as one more amp than the ports left (non-bar: Tesira + UCC-UCX1000-T
  // fallback; bar + expansion: the add caps in room.ts stop short of it).
  // Capacities follow the speaker group (`UCX_SPEAKER_CAPS`). E.g. 3 ceiling
  // mics + 6 C-IC6 → head amps cover 8 → 0 DPA; bar + 2 expansion ceiling mics
  // + 10 C-IC6 → DPA 8 + DCM-XA 2 → 1 DPA; 3 ceiling mics + 2 wall speakers →
  // no head amp for wall, no free port → overflow.
  const micModel = area?.microphones?.model;
  const isCeilingPrimaryMic = Boolean(micModel)
    && MIC_CATEGORIES.CEILING_MOUNTED.has(micModel)
    && !MIC_CATEGORIES.CEILING_TILE.has(micModel)
    && !MIC_CATEGORIES.BAR.has(micModel);
  const isTablePrimaryMic = Boolean(micModel) && MIC_CATEGORIES.TABLETOP.has(micModel);
  const expansionMicQty = isBarPrimary ? placedCount(area?.expansionMicrophones) : 0;
  const hasCeilingChains = isCeilingPrimaryMic || (isBarPrimary && area?.expansionMicType === 'ceiling' && expansionMicQty > 0);
  const hasTableChains = isTablePrimaryMic || (isBarPrimary && area?.expansionMicType === 'table' && expansionMicQty > 0);
  let desiredAmps;
  if (hasCeilingChains || hasTableChains) {
    let caps;
    if (!isBarPrimary) {
      caps = primarySpeakerCaps(area);
    } else {
      caps = expansionSpeakerCaps(area);
    }
    const chainMicQty = isBarPrimary ? expansionMicQty : placedCount(area?.microphones);
    const headAmpCap = hasCeilingChains ? Math.ceil(chainMicQty / CEILING_CHAIN_MAX_MICS) * caps.headAmp : 0;
    if (speakersNeedingAmps === 0 || speakersNeedingAmps <= headAmpCap) {
      desiredAmps = 0;
    } else if (speakersNeedingAmps <= caps.dpa) {
      desiredAmps = 1;
    } else if (hasCeilingChains && speakersNeedingAmps <= caps.dpa + caps.headAmp) {
      desiredAmps = 1;
    } else {
      desiredAmps = Math.max(availableAmpPorts + 1, Math.ceil(speakersNeedingAmps / caps.dpa));
    }
  } else {
    // No mic chains (bar only): both ports are free for DPA-450BP amps (spec §4.4).
    desiredAmps = Math.ceil(speakersNeedingAmps / expansionSpeakerCaps(area).dpa);
  }
  if (desiredAmps <= availableAmpPorts) return null;

  const deficit = desiredAmps - availableAmpPorts;

  // Suggestion picks the freeable port. Expansion mic chains are the easiest
  // lever (user can reduce mics or switch to DCM-XA amplified heads); primary
  // mic chains imply a design-level change. When no chains are present and the
  // overflow comes from sheer speaker count, suggest reducing speakers.
  let suggestion;
  if (usage.breakdown.expansionMicChains > 0) {
    suggestion = 'Reduce expansion microphone count or switch to amplified DCM-XA heads to free a peripheral port.';
  } else if (usage.breakdown.primaryMicChains > 0) {
    suggestion = 'Reduce primary microphone count to free a peripheral port for the amplifier.';
  } else {
    suggestion = 'Reduce speaker count to fit within the available amplifier port(s).';
  }

  return {
    desiredAmps,
    availableAmpPorts,
    deficit,
    speakersNeedingAmps,
    suggestion,
    message: `Configuration needs ${desiredAmps} amplifier port(s) but only ${availableAmpPorts} available. ${suggestion}`,
  };
};

/**
 * Compute the area's current UCC peripheral port usage. Only mic chain consumption
 * is tracked here — amp port consumption is derived implicitly via `remaining`
 * (anything not held by a chain is available for amps), and the actual amp count
 * is decided by the equipment calc using `maxStandaloneAmpPortsFor`.
 *
 * @param {Object|null|undefined} area - Area state (`state.container.areas[activeTab]`)
 * @returns {{
 *   applicable: boolean,
 *   used: number,
 *   max: number,
 *   remaining: number,
 *   exceeds: boolean,
 *   breakdown: {
 *     primaryMicChains: number,
 *     expansionMicChains: number,
 *     dcmxaCount: number,
 *   }
 * }}
 */
export const computePortUsage = (area) => {
  // The UCC's 2-port Ethernet peripheral budget is hardware specific to Tree Frog
  // (Devio UCX, on the Android platform) — plain Devio SCX uses the same AVB
  // network and Parlé TCM-X mics as Tesira, with no UCC hub and no peripheral
  // ports at all. Gating on `productFamily === 'Devio'` alone (without also
  // requiring the Android/UCX platform) wrongly applied this budget to Devio SCX
  // designs too, capping (often to 0 via `maxStandaloneAmpPortsFor`) the
  // standalone-amp count for a hardware constraint that design doesn't have —
  // silently dropping the AMP-450BP a Devio SCX design with speakers actually
  // needs. This mirrors `isDevioUcxDesign` (utilities/treeFrogBundleMatching.js)
  // — duplicated inline rather than imported to avoid a circular import
  // (treeFrogBundleMatching.js already imports `isPortOverflow` from this file).
  // Report `applicable: false` and zero usage so callers can
  // `if (!result.applicable) return` without branching on productFamily/platform
  // themselves.
  if (!area || area.productFamily !== 'Devio' || area.platform !== 'Android') {
    return {
      applicable: false,
      used: 0,
      max: UCC_PERIPHERAL_PORT_BUDGET,
      remaining: UCC_PERIPHERAL_PORT_BUDGET,
      exceeds: false,
      breakdown: { primaryMicChains: 0, expansionMicChains: 0, dcmxaCount: 0 },
    };
  }

  const primaryMicChains = countPrimaryMicChains(area);
  const expansionMicChains = countExpansionMicChains(area);
  const dcmxaCount = countDcmxa(area);

  const used = primaryMicChains + expansionMicChains;
  return {
    applicable: true,
    used,
    max: UCC_PERIPHERAL_PORT_BUDGET,
    remaining: Math.max(0, UCC_PERIPHERAL_PORT_BUDGET - used),
    exceeds: used > UCC_PERIPHERAL_PORT_BUDGET,
    breakdown: { primaryMicChains, expansionMicChains, dcmxaCount },
  };
};

/**
 * Devio UCX capacity table — spec §2.3. Max simultaneous SPEAKERS Devio UCX
 * can support, given the mic TYPE and how many mics are active. Distinct from
 * the port-budget model above (`isPortOverflow`/`computePortUsage`): this is
 * a coverage-capacity question (how many mics/speakers a room's placement
 * algorithm computed as REQUIRED), not a peripheral-port-counting one, and
 * the two are not interchangeable — e.g. Tabletop at 3-4 mics allows 0
 * speakers here even though a port-budget count alone wouldn't say that for
 * every mic/speaker combination in that band.
 *
 * Mic count > 4 is never viable, regardless of type — not a row in this
 * table, handled separately by `micCountBand` returning `null`.
 *
 * SINGLE SOURCE OF TRUTH: this is the only place these speaker-capacity
 * numbers are defined anywhere in the codebase — `isUCXViable` /
 * `isUCXViableForArea` are the only readers, and both go through
 * `micCountBand` below rather than re-deriving the band cutoffs. To update a
 * qty per an amended spec, edit the single cell below; nothing else needs to
 * change. (Do not confuse these with `DCMXA_SPEAKER_CAPACITY` /
 * `DPA_SPEAKER_CAPACITY` further down — those size physical amp hardware
 * channels for the port-budget model, a different question from this table's
 * "how many speakers can UCX's placement/coverage support at all".)
 *
 * AC-8 cross-check ("max number of ports"): for a STANDALONE (non-bar) primary design
 * specifically, these 4 numbers are not arbitrary — they are exactly what the port-budget
 * model above (`checkPortOverflow`'s DCM-XA-promotion + DPA-450BP amp math, gated by
 * `UCC_PERIPHERAL_PORT_BUDGET` = 2) independently computes as the maximum speaker count for
 * the same mic-type/band. Proven algebraically (not just asserted) in
 * `treeFrogPorts.test.js`'s "AC-8 cross-check" suite, which brute-forces the port-budget
 * maximum via `checkPortOverflow` and checks it against this table's value for all 4 cells —
 * if either side is ever edited without the other, that suite fails.
 */
const UCX_CAPACITY_TABLE = {
  ceiling: {
    '1-2': 12, // 1-2 ceiling (DCM-X) mics → up to 12 speakers
    '3-4': 8,  // 3-4 ceiling (DCM-X) mics → up to 8 speakers
  },
  table: {
    '1-2': 8, // 1-2 table (DTM-X) mics → up to 8 speakers
    '3-4': 0, // 3-4 table (DTM-X) mics → 0 speakers (not viable with any speakers)
  },
};

/**
 * Port accounting spec §6.1 for a non-bar design, by speaker group:
 *   ceiling 1–2 mics: one port free → DCM-XA + DPA-450BP; 3–4 mics: two DCM-XA.
 *   table   1–2 mics: one DPA-450BP;                       3–4 mics: no port left.
 * With the C-IC6 capacities this reproduces UCX_CAPACITY_TABLE (12 / 8 / 8 / 0);
 * P6-SM gives 6 / 4 / 4 / 0 and wall 2 / 0 / 2 / 0.
 */
const maxPrimarySpeakersFor = (micType, band, caps) => {
  if (micType === 'ceiling') return band === '1-2' ? caps.headAmp + caps.dpa : 2 * caps.headAmp;
  if (micType === 'table') return band === '1-2' ? caps.dpa : 0;
  return 0;
};

/** '1-2' | '3-4' band for a given mic count, or null when out of Devio UCX's range entirely. */
const micCountBand = (micCount) => {
  if (micCount <= 2) return '1-2';
  if (micCount <= 4) return '3-4';
  return null;
};

/**
 * Devio UCX viability check — spec §2.3. Answers "is THIS design, as it
 * currently stands, still within Devio UCX's mic/speaker capacity for the
 * given mic type" — the trigger for the Case 2/3 capacity fallback (remove
 * bar, swap Platform/Family per androidExplicitlySelected — spec §2.2). Also
 * reused (see room.ts's `computeMicTypeSwitchFlag`) to evaluate a CANDIDATE
 * mic type for the ceiling↔tabletop expansion-type switch UI — that
 * consuming behavior is an implementation safeguard, not itself a numbered
 * spec section, even though the capacity math it calls into here is.
 *
 * Self-disabling by design: unless `assumeApplicable` is passed, this reads
 * the area's REAL current `productFamily`/`platform`. A design that has
 * already fallen back to Tesira/Devio SCX has no Devio UCX capacity
 * constraint left to enforce, so it's trivially viable — callers don't need
 * to check productFamily/platform themselves before calling this.
 *
 * `assumeApplicable` (default false) opts INTO the hypothetical-Devio+Android
 * normalization `isPortOverflow` already does for the platform-selector
 * eligibility check — for a design that is exclusive (bar + expansion) or is
 * ABOUT to become exclusive on this same dispatch, before `platform`/
 * `productFamily` have actually been flipped to Android/Devio yet. Without
 * this, the very FIRST dispatch that makes a design exclusive (e.g. selecting
 * a bar mic when the room's real ceiling-expansion mic count already exceeds
 * capacity) reads `platform !== 'Android'` as still true, self-disables this
 * check, and lets `revalidateUcxEligibility`'s exclusivity branch commit
 * platform=Android + its toast BEFORE the real mic/speaker count is ever
 * checked — see room.ts's `revalidateUcxEligibility`/`attemptCeilingHeight
 * Mitigation`/`attemptTableToCeilingSwitch`, all of which pass this through
 * for exactly that reason.
 *
 * @param {'ceiling'|'table'} micType - the mic type to evaluate: the design's
 *   OWN active type (drives the §2.2 fallback trigger) or a CANDIDATE type
 *   being considered for a ceiling↔tabletop switch (drives the switch-
 *   availability safeguard — not a spec-numbered rule, see room.ts's
 *   `computeMicTypeSwitchFlag`) — same function, different argument.
 * @param {Object} area
 * @param {{ assumeApplicable?: boolean }} [options]
 * @returns {{
 *   viable: boolean,
 *   applicable: boolean,
 *   micCount: number,
 *   speakerCount: number,
 *   maxSpeakers: number,
 * }}
 */
export const isUCXViable = (micType, area, options = {}) => {
  const { assumeApplicable = false } = options;
  if (!area || (!assumeApplicable && (area.productFamily !== 'Devio' || area.platform !== 'Android'))) {
    // Not currently (or hypothetically) Devio UCX — no capacity constraint applies. See doc
    // comment above.
    return { viable: true, applicable: false, micCount: 0, speakerCount: 0, maxSpeakers: Infinity };
  }

  // Bar + expansion: the expansion mics/speakers are what's evaluated (the
  // bar mic itself is a fixed USB array, not a chained peripheral competing
  // for capacity). Standalone (no bar) primary Ceiling/Table design: the
  // primary mics/speakers are what's evaluated. These are mutually exclusive
  // shapes — a design has expansion mics only when its primary is Bar.
  const hasExpansion = area.expansionMicType === 'ceiling' || area.expansionMicType === 'table';
  const micCount = hasExpansion
    ? (area.expansionMicrophones?.layout?.length ?? area.expansionMicrophones?.qty ?? 0)
    : (area.microphones?.layout?.length ?? area.microphones?.qty ?? 0);
  const speakerCount = hasExpansion
    ? (area.expansionSpeakers?.layout?.length ?? area.expansionSpeakers?.qty ?? 0)
    : (area.speakers?.layout?.length ?? area.speakers?.qty ?? 0);

  const band = micCountBand(micCount);
  if (!band) {
    // > 4 mics — never viable, regardless of type (spec §2.3).
    return { viable: false, applicable: true, micCount, speakerCount, maxSpeakers: 0 };
  }

  // Non-bar designs: capacity depends on the primary speaker group (port
  // accounting spec §6.1). The table above is the C-IC6 column; pendant and wall
  // use the same port model with their own per-amp capacities.
  const maxSpeakers = hasExpansion
    ? (UCX_CAPACITY_TABLE[micType]?.[band] ?? 0)
    : maxPrimarySpeakersFor(micType, band, primarySpeakerCaps(area));
  return {
    viable: speakerCount <= maxSpeakers,
    applicable: true,
    micCount,
    speakerCount,
    maxSpeakers,
  };
};

/**
 * Convenience wrapper for a standalone (no bar) design: derives `micType`
 * from the PRIMARY mic model via `getPrimaryMicType` rather than requiring
 * the caller to know it's 'ceiling' or 'table'. Returns viable:true (a no-op
 * result) for any primary type `isUCXViable`'s table doesn't cover (Bar,
 * Ceiling Tile, Pendant) — those are hard-filtered elsewhere
 * (`isDevioUcxEligible`), not this function's concern.
 */
export const isUCXViableForArea = (area, options = {}) => {
  const hasExpansion = area?.expansionMicType === 'ceiling' || area?.expansionMicType === 'table';
  const micType = hasExpansion ? area.expansionMicType : getPrimaryMicType(area?.microphones?.model);
  if (micType !== 'ceiling' && micType !== 'table') {
    return { viable: true, applicable: false, micCount: 0, speakerCount: 0, maxSpeakers: Infinity };
  }
  return isUCXViable(micType, area, options);
};